//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import search from 'assets/search.svg';
import { ReactComponent as Clear } from 'assets/x-circle.svg';
import { useEffect, useRef, useState } from 'react';

const SearchInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  clearable = false,
  maxWidthPx = 256,
  allowEnterSubmit = false, // 預設 false，阻止 Enter 觸發表單 Submit
  onKeyDown,
  className,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
  clearable?: boolean;
  maxWidthPx?: number;
  allowEnterSubmit?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);
  const [searchFocus, setSearchFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [width, setWidth] = useState<number>();

  useEffect(() => {
    if (inputRef.current && searchFocus) {
      setWidth(inputRef.current.offsetWidth);
    } else {
      setWidth(undefined);
    }
  }, [searchFocus]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleClear = () => {
    setValue('');
    onChange('');
    inputRef.current?.focus();
  };

  const showClearButton = clearable && searchFocus && !!value;

  return (
    <div
      className="border flex items-center rounded w-full min-w-[64px] text-sm font-normal bg-base-100 dark:bg-gray-800"
      style={{
        borderColor: 'hsl(var(--bc) / 0.3)',
        maxWidth: maxWidthPx,
        ...(clearable && searchFocus ? { outline: '2px solid hsla(var(--bc) / 0.2)', outlineOffset: 2 } : {}),
      }}
    >
      <input
        {...props}
        ref={inputRef}
        className={clearable ? `!flex-1 !min-w-0 !w-auto !outline-none ${className ?? ''}` : className}
        style={
          clearable
            ? undefined
            : {
                width: width ? `${width}px` : '100%',
              }
        }
        value={value}
        autoFocus={searchFocus ? true : false}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !allowEnterSubmit) {
            e.preventDefault();
          }
          onKeyDown?.(e); // 若外部需要調用
        }}
        onFocus={() => {
          if (inputRef.current) {
            const currentWidth = inputRef.current.offsetWidth;
            const willShowClearButton = clearable && !!value;
            setWidth(willShowClearButton ? currentWidth : currentWidth + 28);
          }
          setSearchFocus(true);
        }}
        onBlur={() => {
          setWidth(undefined);
          setSearchFocus(false);
        }}
        data-testid="search-input"
      />
      {showClearButton ? (
        <button
          type="button"
          className="mr-2 w-[14px] h-[14px] flex-shrink-0 flex items-center justify-center rounded-full transition-colors text-[#597393] bg-white hover:text-[#C7CCD2] hover:bg-[#597393]"
          // 避免點擊時瀏覽器預設行為把 focus 從 <input> 搶走，讓清空後游標可以留在輸入框裡
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          aria-label="clear search"
          data-testid="search-input-clear-btn"
        >
          <Clear className="w-[14px] h-[14px] scale-[1.4]" />
        </button>
      ) : (
        !searchFocus && <img src={search} className="w-5 h-5 mr-2 cyfi-svg dark:svg-dark-mode" alt="search" />
      )}
    </div>
  );
};
export default SearchInput;
